<template>
  <div>
    <div class="multi-select-dropdown__header" v-if="!hideHeader">
      <button @click="selectAll()" class="multi-select-dropdown__button">Select All</button>
      <button @click="clearAll()" class="multi-select-dropdown__button ">Clear All</button>
    </div>
    <div class="multi-select-dropdown__separator"></div>
    <div class="multi-select-dropdown__input-container" v-if="isSmartInput">
      <input class="multi-select-dropdown__input" type="text" v-model="inputModel">
      <div class="multi-select-dropdown__clear-button" @click.stop="close()" v-if="inputModel"></div>
      <base-material-design-icon class="multi-select-dropdown__search-button" icon="search" v-else></base-material-design-icon>
    </div>
    <ul class="multi-select-dropdown__list">
      <li v-for="option in filteredOptions"
          class="multi-select-dropdown__item"
          :class="{
            'multi-select-dropdown__item_selected': isSelected(option),
            'multi-select-dropdown__item_pointer': singleSelect
            }"
          @click="toggleOptionState(option)" :key="option.value" v-html="listLabel(option)">
      </li>
    </ul>
  </div>
</template>

<script>
import _ from 'lodash'
const MIN_CHARS_NUMBER = 1

/**
 * component is used in {@link BaseMultiSelectSortableHeader} as filter for array value like sections, statuses
 */
export default {
  name: 'BaseMultiSelectDropdown',
  model: {
    prop: 'modelValue',
    event: 'change'
  },
  props: {
    /**
     * list of all available options for dropdown list
     * */
    options: {
      type: Array,
      default: () => []
    },
    /**
     * this prop for options assigned outside component
     * selected options is array of selected ids
     * */
    predefinedFilter: {
      type: Array,
      default: () => []
    },
    /**
     * enable smart input functionality
     * */
    isSmartInput: {
      type: Boolean,
      default: true
    },
    hideHeader: {
      type: Boolean,
      default: false
    },
    /**
     * disable multiselect mode
     * */
    singleSelect: {
      type: Boolean,
      default: false
    },
    /**
     * modelValue reflects whether something is applied in this component or not to change style of parent object
     * */
    modelValue: {
      default: false
    }
  },
  data () {
    return {
      inputModel: null,
      // contains available options
      clonedOptions: [],
      // contains filtered and stylized options depending on entered string value (inputModel)
      filteredOptions: [],
      // contains map of objects with key == id, for saving current selection
      selectedOptionsMap: {},
      // applied selected data
      appliedData: {}
    }
  },
  watch: {
    filter: {
      deep: true,
      handler (newVal, oldVal) {
        if (!_.isEqual(oldVal, newVal)) {
          this.$emit('selectionChanged', newVal)
        }
      }
    },
    inputModel () {
      this.filterOptions()
    },
    predefinedFilter: {
      handler (value) {
        if (value && Array.isArray(value)) {
          // mark filter dropdown as selected (showHighlight) in BaseMultiSelectSortableHeader in case any default values are passed inside of it
          this.selectedOptionsMap = _.cloneDeep(value.reduce((ac, cv) => { ac[cv] = this.options.find(item => item.value === cv); return ac }, {}))
          this.appliedData = { ...this.selectedOptionsMap }
          this.$emit('change', this.somethingIsApplied())
        }
      },
      immediate: true
    },
    options: {
      handler: function () {
        this.clonedOptions = _.cloneDeep(this.options)
        this.filteredOptions = this.clonedOptions
      },
      immediate: true
    }
  },
  computed: {
    filter () {
      return Object.values(this.selectedOptionsMap).filter(item => item).map(item => item.value)
    }
  },
  methods: {
    /**
     * select/unselect clicked item
     * @param item - clicked option
     * */
    toggleOptionState (item) {
      if (this.singleSelect) {
        Object.keys(this.selectedOptionsMap).forEach(key => {
          this.selectedOptionsMap[key] = null
        })
        this.$set(this.selectedOptionsMap, item.value, item)
      } else {
        if (this.selectedOptionsMap[item.value]) {
          this.selectedOptionsMap[item.value] = null
        } else {
          this.$set(this.selectedOptionsMap, item.value, item)
        }
      }
    },
    filterOptions () {
      if (!this.debouncedFilterFunc) {
        this.debouncedFilterFunc = _.debounce(this.calculateFilteredOptions, 100)
      } else {
        this.debouncedFilterFunc.cancel()
      }
      this.debouncedFilterFunc()
    },
    /**
     * method stylizes dropdown list based on input text
     * */
    calculateFilteredOptions () {
      if (!this.inputModel || this.inputModel.length < MIN_CHARS_NUMBER) {
        this.clearStyle()
        this.filteredOptions = this.clonedOptions
      } else {
        this.filteredOptions = this.clonedOptions.filter(item => {
          const label = String(item.label)
          // code styling list item label
          if (label.toLowerCase().includes(this.inputModel.toLowerCase())) {
            const split = label.split(this.inputModel)
            item.stylizedLabel = split[ 0 ]
            for (let i = 1; i < split.length; i++) {
              item.stylizedLabel += `<span class="multi-select-dropdown__item_entered">${this.inputModel}</span>${split[ i ]}`
            }
            return true
          }
          return false
        })
      }
    },
    listLabel (option) {
      if (option.stylizedLabel) {
        return option.stylizedLabel
      }
      return option.label
    },
    selectAll () {
      this.clonedOptions.forEach(item => {
        // some Vue reactivity stuff
        this.$set(this.selectedOptionsMap, item.value, item)
      })
    },
    clearAll () {
      this.selectedOptionsMap = {}
    },
    clearStyle () {
      this.clonedOptions.forEach(item => { item.stylizedLabel = null })
    },
    close () {
      this.inputModel = ''
      this.clearStyle()
      this.filteredOptions = this.clonedOptions
    },
    cancel () {
      this.selectedOptionsMap = {}
      this.appliedData = {}
      this.$emit('change', this.somethingIsApplied())
      this.close()
    },
    apply () {
      const filter = Object.values(this.selectedOptionsMap).filter(item => item).map(item => item.value)
      this.appliedData = this.selectedOptionsMap
      this.$emit('apply', filter)
      this.close()
    },
    isSelected (item) {
      return this.selectedOptionsMap ? Boolean(this.selectedOptionsMap[ item.value ]) : false
    },
    /**
     * invoked from parent component when component is opened/closed
     * @param isOpened
     */
    reset (isOpened) {
      if (isOpened) {
        this.selectedOptionsMap = {}
        Object.values(this.appliedData).forEach(item => {
          if (item && item.value) {
            this.$set(this.selectedOptionsMap, item.value, item)
          }
        })
      } else {
        this.$emit('change', this.somethingIsApplied())
        this.close()
      }
    },
    somethingIsApplied () {
      return Object.values(this.appliedData).some(value => value !== null)
    }
  }
}
</script>
<!--do not add "scoped" here!-->
<style  lang="scss">
  .multi-select-dropdown {
    position: relative;
    flex: 1 1 100%;
    height: 100%;

    &__highlight {
      border-bottom: 2px solid $rfx-bright-blue;
    }

    &__separator{
      background-color: $rfx-light-steel;
      margin: 5px 0 0 15px;
      height: 1px;
    }

    &__selected {
      background: $rfx-light-grey;
    }

    &__header {
      display: flex;
      flex-direction: column;
      height: 100%;
      box-sizing: border-box;
      padding: 5px 0;
      justify-content: space-around;
      margin-top: 3px;
    }

     &__sort-container{
      padding-top: 4px;
    }

    &__dropdown {
      position: absolute;
      border-top: 2px solid $rfx-bright-blue;
      z-index: 9999;
      top: 100%;
      background-color: #fff;
      box-shadow: 0 0 1px 0 rgba(8, 8, 8, .24), 0 4px 15px 0 rgba(8, 8, 8, .24);
      cursor: auto;
      border-radius: 0 0 6px 6px;
    }

    &__list {
      list-style-type: none;
      overflow-x: hidden;
      overflow-y: auto;
      font-weight: normal;
      padding: 0;
      margin: 5px 0 0 0;
      max-height: 215px;

    }

    &__clear-button,
    &__search-button {
      position: absolute;
      top: 13px;
      right: 7px;
      width: 16px;
      height: 16px;
      background-color: transparent;
      color: $rfx-dark-march;
    }

    &__clear-button {
      cursor: pointer;
      &:hover {
        opacity: 1;
      }

      &:before, &:after {
        position: absolute;
        left: 7px;
        top: 3px;
        content: '';
        height: 12px;
        width: 2px;
        background-color: currentColor;
      }

      &:before {
        transform: rotate(45deg);
      }

      &:after {
        transform: rotate(-45deg);
      }
    }
    &__input-container {
      position: relative;
      display: flex;
      padding: 0 10px 0 15px;
      margin: 0;
    }

    &__input {
      border: none;
      border-bottom: 1px solid $rfx-bright-blue;
      height: 20px;
      flex: 1 1 100%;
      padding: 10px 0;
      width: 100%;
      outline: none;
    }

    &__item {
      height: 30px;
      line-height: 30px;
      padding: 0 15px;
      @include ellipsis;
      font-size: 12px;
      color: #000;
      white-space: initial;
      user-select: none;

      &_selected {
        color: #fff !important;
        background-color: $rfx-bright-blue;
      }

      &_selected * {
        color: #fff !important;
      }

      &_pointer {
        cursor: pointer;
      }

      &:hover:not(.multi-select-dropdown__item_selected) {
        color: #000 !important;
        background-color: rgba(1,22,39,.1);
      }

      &:hover * {
        color: #fff !important;
      }

      &_entered {
        color: $rfx-light-blue;
      }
    }

    &__button {
      border: none;
      outline: none;
      cursor: pointer;
      font-size: 12px;
      width: fit-content;
      padding: 5px 15px;
      background-color: transparent;
      color: $rfx-dark-march;
      user-select: none;

      &:hover{
        color: $rfx-bright-blue;
      }
    }

  }

</style>
